<script lang="ts">
  // Import past Strava activities: the ones in a date range that aren't in Xert yet (and weren't
  // sent to Strava from Xert), of the synced types. Pick some and import them.
  import { Button } from '$lib/components/ui/button'
  import * as Card from '$lib/components/ui/card'
  import { Checkbox } from '$lib/components/ui/checkbox'
  import { Input } from '$lib/components/ui/input'
  import { getStravaActivities, importStravaActivities, type StravaListedActivity } from '@/Util/api'
  import { ErrorCode } from '@/Util/errorCodes'
  import { notifyApiError, showSuccessToast } from '@/Util/notify'
  import { formatElapsed, formatStart, typeLabel } from './strava'

  /** The optional types picked, besides the sport's own (the server adds those). */
  export let types: string[]
  export let distanceUnits: string
  export let canSync = true

  // Strava history goes back three years here, as on the original page.
  const DAY = 86400000
  const iso = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
  const today = new Date()
  const minDate = iso(new Date(today.getTime() - 366 * 3 * DAY))
  const maxDate = iso(today)

  const PRESETS = [
    { id: 'today', label: 'Today', range: () => [today, today] },
    { id: 'yesterday', label: 'Yesterday', range: () => [new Date(today.getTime() - DAY), new Date(today.getTime() - DAY)] },
    { id: '7', label: 'Last 7 days', range: () => [new Date(today.getTime() - 6 * DAY), today] },
    { id: '30', label: 'Last 30 days', range: () => [new Date(today.getTime() - 29 * DAY), today] },
    { id: 'month', label: 'This month', range: () => [new Date(today.getFullYear(), today.getMonth(), 1), today] },
    { id: 'last-month', label: 'Last month', range: () => [new Date(today.getFullYear(), today.getMonth() - 1, 1), new Date(today.getFullYear(), today.getMonth(), 0)] },
    { id: 'year', label: 'Last year', range: () => [new Date(today.getFullYear() - 1, today.getMonth(), today.getDate()), today] },
    { id: '2-years', label: 'Last 2 years', range: () => [new Date(today.getFullYear() - 2, today.getMonth(), today.getDate()), today] },
  ] as const
  type PresetId = (typeof PRESETS)[number]['id'] | 'custom'

  let preset: PresetId = '30'
  let from = iso(PRESETS[3].range()[0])
  let to = maxDate

  function choose(id: PresetId) {
    preset = id
    const p = PRESETS.find((p) => p.id === id)
    if (p) [from, to] = p.range().map(iso)
  }

  function customFrom(value: string) {
    preset = 'custom'
    from = value < minDate ? minDate : value
    if (to < from) to = from
  }
  function customTo(value: string) {
    preset = 'custom'
    to = value > maxDate ? maxDate : value
    if (from > to) from = to
  }

  let rows: StravaListedActivity[] = []
  let loading = true
  let failed = false
  let request = 0

  async function load(from: string, to: string, typesKey: string) {
    if (!from || !to) return
    const types = typesKey ? typesKey.split(',') : []
    const id = ++request
    loading = true
    failed = false
    const after = Math.floor(new Date(`${from}T00:00:00`).getTime() / 1000)
    const before = Math.floor(new Date(`${to}T23:59:59`).getTime() / 1000)
    const res = await getStravaActivities(after, before, types)
    if (id !== request) return // a newer range is loading
    loading = false
    if (!res.success) {
      failed = true
      rows = []
      notifyApiError(res, { code: ErrorCode.STRAVA_ACTIVITIES_FAILED, message: 'Could not load your Strava activities. Please try again.' })
      return
    }
    rows = res.data ?? []
    selected = new Set([...selected].filter((id) => rows.some((r) => r.id === id)))
    page = 0
  }
  // A string, so a new array with the same types doesn't load the list again.
  $: typesKey = types.join(',')
  $: load(from, to, typesKey)

  // Searching, sorting and paging.
  type SortKey = 'date' | 'name' | 'type' | 'distance' | 'time'
  let search = ''
  let sortKey: SortKey = 'date'
  let sortAsc = false
  let page = 0
  let pageSize = 10
  const PAGE_SIZES = [10, 25, 50, 100]

  const sortValue: Record<SortKey, (r: StravaListedActivity) => string | number> = {
    date: (r) => r.start_date_local,
    name: (r) => (r.name ?? '').toLowerCase(),
    type: (r) => typeLabel(r.type),
    distance: (r) => r.distance ?? 0,
    time: (r) => r.elapsed_time ?? 0,
  }

  function sortBy(key: SortKey) {
    if (sortKey === key) sortAsc = !sortAsc
    else {
      sortKey = key
      sortAsc = key === 'name' || key === 'type'
    }
  }

  $: query = search.trim().toLowerCase()
  $: filtered = rows
    .filter((r) => !query || `${r.name} ${typeLabel(r.type)} ${formatStart(r.start_date_local).date}`.toLowerCase().includes(query))
    .sort((a, b) => {
      const va = sortValue[sortKey](a), vb = sortValue[sortKey](b)
      return (va < vb ? -1 : va > vb ? 1 : 0) * (sortAsc ? 1 : -1)
    })
  $: pages = Math.max(1, Math.ceil(filtered.length / pageSize))
  $: if (page >= pages) page = pages - 1
  $: shown = filtered.slice(page * pageSize, (page + 1) * pageSize)
  $: query, pageSize, (page = 0)

  // Selection, by Strava id. "Select all" covers every activity the search matches.
  let selected = new Set<number>()
  $: allChecked = filtered.length > 0 && filtered.every((r) => selected.has(r.id))
  $: someChecked = !allChecked && filtered.some((r) => selected.has(r.id))

  function toggle(id: number, on: boolean) {
    if (on) selected.add(id)
    else selected.delete(id)
    selected = selected
  }
  function toggleAll(on: boolean) {
    for (const r of filtered) on ? selected.add(r.id) : selected.delete(r.id)
    selected = selected
  }

  let importing = false
  async function importSelected() {
    const picked = rows.filter((r) => selected.has(r.id))
    if (!picked.length) return
    importing = true
    const res = await importStravaActivities(picked)
    if (!res.success) {
      importing = false
      notifyApiError(res, { code: ErrorCode.STRAVA_IMPORT_FAILED, message: 'Could not import those activities. Please try again.' })
      return
    }
    rows = rows.filter((r) => !selected.has(r.id))
    selected = new Set()
    showSuccessToast(`Importing ${picked.length} ${picked.length === 1 ? 'activity' : 'activities'}. Taking you to My Fitness, where your progression updates as Xert processes them.`)
    setTimeout(() => (window.location.href = '/'), 3000)
  }

  $: status = !canSync
    ? "Free accounts can't import activities at this time."
    : selected.size
      ? `${selected.size} selected`
      : !loading && rows.length
        ? `${rows.length} ${rows.length === 1 ? 'activity' : 'activities'} to import`
        : ''
  $: importLabel = `Import ${selected.size || ''} ${selected.size === 1 ? 'activity' : 'activities'}`.replace('  ', ' ')

  const distance = (r: StravaListedActivity) => (r.distance ? `${Number(r.distance).toFixed(2)} ${distanceUnits}` : '—')

  const COLUMNS: { key: SortKey, label: string, class: string }[] = [
    { key: 'date', label: 'Date', class: 'hidden md:table-cell w-[180px]' },
    { key: 'name', label: 'Name', class: '' },
    { key: 'type', label: 'Type', class: 'hidden sm:table-cell w-[130px]' },
    { key: 'distance', label: 'Distance', class: 'hidden sm:table-cell w-[110px] text-right' },
    { key: 'time', label: 'Time', class: 'hidden md:table-cell w-[90px] text-right' },
  ]
</script>

<Card.Root>
  <Card.Header class="gap-1 pb-3">
    <Card.Title class="text-base">Import past activities</Card.Title>
    <Card.Description>
      Strava activities from the last three years that aren't in Xert yet. Activities Xert sent to Strava aren't listed.
    </Card.Description>
  </Card.Header>
  <Card.Content class="flex flex-col gap-3">
    <!-- The chosen range (Custom for dates typed in) shows a spinner while its activities load. -->
    <div class="flex flex-wrap gap-1.5" role="radiogroup" aria-label="Date range" aria-busy={loading}>
      {#each PRESETS as p (p.id)}
        <button type="button" role="radio" aria-checked={preset === p.id} on:click={() => choose(p.id)}
          class="h-8 cursor-pointer rounded-full border px-3 text-xs font-medium transition-colors
            {preset === p.id ? 'border-primary bg-primary text-primary-foreground' : 'border-border bg-background text-foreground hover:bg-accent'}">
          {p.label}
          {#if loading && preset === p.id}<i class="fa-solid fa-spinner-third fa-spin ml-1.5" aria-hidden="true" /><span class="sr-only">Loading</span>{/if}
        </button>
      {/each}
      <button type="button" role="radio" aria-checked={preset === 'custom'} on:click={() => (preset = 'custom')}
        class="h-8 cursor-pointer rounded-full border px-3 text-xs font-medium transition-colors
          {preset === 'custom' ? 'border-primary bg-primary text-primary-foreground' : 'border-border bg-background text-foreground hover:bg-accent'}">
        Custom
        {#if loading && preset === 'custom'}<i class="fa-solid fa-spinner-third fa-spin ml-1.5" aria-hidden="true" /><span class="sr-only">Loading</span>{/if}
      </button>
    </div>

    <div class="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
      <div class="flex items-end gap-2">
        <label class="m-0 flex flex-col gap-1 text-xs font-medium text-muted-foreground">
          From
          <input type="date" value={from} min={minDate} max={maxDate} on:change={(e) => customFrom(e.currentTarget.value)}
            class="h-9 rounded-md border border-input bg-background px-2 text-sm text-foreground" />
        </label>
        <label class="m-0 flex flex-col gap-1 text-xs font-medium text-muted-foreground">
          To
          <input type="date" value={to} min={minDate} max={maxDate} on:change={(e) => customTo(e.currentTarget.value)}
            class="h-9 rounded-md border border-input bg-background px-2 text-sm text-foreground" />
        </label>
      </div>
      <div class="relative sm:w-[260px]">
        <i class="fa-solid fa-magnifying-glass pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground" aria-hidden="true" />
        <Input type="search" placeholder="Search activities" aria-label="Search activities" bind:value={search} class="pl-8" />
      </div>
    </div>

    <!-- Selection bar above the table, so the count, Deselect and Import show without scrolling. -->
    <div class="flex min-h-[52px] flex-col gap-2 rounded-lg px-3 py-2 transition-colors sm:flex-row sm:items-center sm:justify-between
      {selected.size ? 'bg-primary/10' : 'bg-muted/40'}">
      {#if selected.size && canSync}
        <span class="flex items-center gap-4 text-sm" aria-live="polite">
          <span class="font-semibold text-foreground">{selected.size} {selected.size === 1 ? 'activity' : 'activities'} selected</span>
          <button type="button" on:click={() => (selected = new Set())}
            class="inline-flex cursor-pointer items-center gap-1.5 border-0 bg-transparent p-0 font-medium text-primary hover:underline">
            <i class="fa-solid fa-xmark" aria-hidden="true" /> Deselect
          </button>
        </span>
      {:else}
        <span class="text-sm text-muted-foreground" aria-live="polite">{status}</span>
      {/if}
      <Button on:click={importSelected} disabled={!canSync || !selected.size || importing} class="sm:min-w-[180px]">
        {#if importing}
          <i class="fa-solid fa-spinner-third fa-spin mr-2" aria-hidden="true" />Importing...
        {:else}
          <i class="fa-solid fa-cloud-arrow-down mr-2" aria-hidden="true" />{importLabel}
        {/if}
      </Button>
    </div>

    <div class="overflow-hidden rounded-lg border border-border">
      <table class="m-0 w-full border-collapse text-sm">
        <thead class="bg-muted/50">
          <tr class="border-b border-border">
            <th class="w-10 px-3 py-2 text-left">
              <Checkbox checked={someChecked ? 'indeterminate' : allChecked} disabled={!filtered.length}
                onCheckedChange={(v) => toggleAll(v === true)} aria-label="Select all activities" />
            </th>
            {#each COLUMNS as c (c.key)}
              <th class="px-3 py-2 text-left text-xs font-medium text-muted-foreground {c.class}"
                aria-sort={sortKey === c.key ? (sortAsc ? 'ascending' : 'descending') : 'none'}>
                <button type="button" on:click={() => sortBy(c.key)}
                  class="inline-flex cursor-pointer items-center gap-1 border-0 bg-transparent p-0 font-medium text-inherit hover:text-foreground">
                  {c.label}
                  <i class="fa-solid text-[10px] {sortKey === c.key ? (sortAsc ? 'fa-arrow-up' : 'fa-arrow-down') : 'fa-sort opacity-40'}" aria-hidden="true" />
                </button>
              </th>
            {/each}
          </tr>
        </thead>
        <tbody>
          {#if loading}
            {#each Array(4) as _}
              <tr class="border-b border-border last:border-0">
                <td class="px-3 py-3"><div class="h-4 w-4 animate-pulse rounded bg-muted" /></td>
                {#each COLUMNS as c}
                  <td class="px-3 py-3 {c.class}"><div class="h-4 w-3/4 animate-pulse rounded bg-muted" /></td>
                {/each}
              </tr>
            {/each}
          {:else if !shown.length}
            <tr>
              <td colspan={COLUMNS.length + 1} class="px-3 py-10 text-center text-sm text-muted-foreground">
                {#if failed}
                  Your Strava activities could not be loaded.
                  <button type="button" class="cursor-pointer border-0 bg-transparent p-0 font-medium text-primary hover:underline" on:click={() => load(from, to, typesKey)}>Try again</button>
                  <span class="mt-1 block text-xs">If this keeps happening, disconnect Strava and connect again.</span>
                {:else if query}
                  No activities match "{search.trim()}".
                {:else}
                  <i class="fa-solid fa-circle-check mb-2 block text-2xl text-green-600" aria-hidden="true" />
                  Everything from Strava in this range is already in Xert.
                {/if}
              </td>
            </tr>
          {:else}
            {#each shown as r (r.id)}
              {@const start = formatStart(r.start_date_local)}
              <tr class="cursor-pointer border-b border-border last:border-0 hover:bg-muted/40 {selected.has(r.id) ? 'bg-primary/5' : ''}"
                on:click={(e) => { if (!(e.target instanceof Element && e.target.closest('button'))) toggle(r.id, !selected.has(r.id)) }}>
                <td class="px-3 py-2.5">
                  <Checkbox checked={selected.has(r.id)} onCheckedChange={(v) => toggle(r.id, v === true)} aria-label="Select {r.name}" />
                </td>
                <td class="hidden px-3 py-2.5 text-muted-foreground md:table-cell">
                  <span class="block text-foreground">{start.date}</span>
                  <span class="text-xs">{start.time}</span>
                </td>
                <td class="px-3 py-2.5">
                  <span class="block font-medium text-foreground">{r.name || 'Untitled activity'}</span>
                  <span class="mt-0.5 flex flex-wrap gap-x-1 text-xs text-muted-foreground md:hidden">
                    <span class="whitespace-nowrap">{start.date}</span>
                    <span class="whitespace-nowrap sm:hidden">· {typeLabel(r.type)}</span>
                    <span class="whitespace-nowrap sm:hidden">· {distance(r)}</span>
                    <span class="whitespace-nowrap">· {formatElapsed(r.elapsed_time)}</span>
                  </span>
                </td>
                <td class="hidden px-3 py-2.5 sm:table-cell">
                  <span class="rounded-full bg-secondary px-2 py-0.5 text-xs text-secondary-foreground">{typeLabel(r.type)}</span>
                </td>
                <td class="hidden px-3 py-2.5 text-right tabular-nums sm:table-cell">{distance(r)}</td>
                <td class="hidden px-3 py-2.5 text-right tabular-nums md:table-cell">{formatElapsed(r.elapsed_time)}</td>
              </tr>
            {/each}
          {/if}
        </tbody>
      </table>
    </div>

    {#if !loading && filtered.length > PAGE_SIZES[0]}
      <div class="flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground">
        <label class="m-0 flex items-center gap-2 font-normal">
          Show
          <select bind:value={pageSize} class="h-8 rounded-md border border-input bg-background px-1 text-xs text-foreground">
            {#each PAGE_SIZES as n}<option value={n}>{n}</option>{/each}
          </select>
        </label>
        <span>{page * pageSize + 1}–{Math.min(filtered.length, (page + 1) * pageSize)} of {filtered.length}</span>
        <div class="flex gap-1">
          <Button variant="outline" size="sm" disabled={page === 0} on:click={() => page--} aria-label="Previous page">
            <i class="fa-solid fa-chevron-left" aria-hidden="true" />
          </Button>
          <Button variant="outline" size="sm" disabled={page >= pages - 1} on:click={() => page++} aria-label="Next page">
            <i class="fa-solid fa-chevron-right" aria-hidden="true" />
          </Button>
        </div>
      </div>
    {/if}

    <div class="flex flex-col gap-2 border-t border-border pt-3 sm:flex-row sm:items-center sm:justify-between">
      <span class="text-sm text-muted-foreground">{status}</span>
      <Button on:click={importSelected} disabled={!canSync || !selected.size || importing} class="sm:min-w-[180px]">
        {#if importing}
          <i class="fa-solid fa-spinner-third fa-spin mr-2" aria-hidden="true" />Importing...
        {:else}
          <i class="fa-solid fa-cloud-arrow-down mr-2" aria-hidden="true" />{importLabel}
        {/if}
      </Button>
    </div>
  </Card.Content>
</Card.Root>
